import React from 'react'
import { Box } from '@mui/material'
import { useDroppable } from '@dnd-kit/core'
import { DropTarget } from '../../../utils/dashboardBuilderLayout'

interface LayoutDropZoneProps {
  id: string
  target: DropTarget
  /** 'row' = full-width horizontal strip between rows. 'column' = thin vertical strip inside a row. */
  orientation: 'row' | 'column'
  /** Row-gap zones on an otherwise-empty canvas render taller with a prompt. */
  emphasize?: boolean
  /** Prompt shown on an emphasized zone. */
  emptyLabel?: string
}

/**
 * A drop target rendered as a thin strip that expands and lights up (info
 * blue) while something is dragged over it. Both new-widget-from-palette and
 * move-existing-widget drags land on the same zones — the surface's
 * `onDragEnd` decides which mutation to apply based on what is being dragged.
 *
 * Shared by the template builder canvas and the dashboard editor, so a drop at
 * a row seam means the same thing in both (see layoutDnd.ts).
 */
const LayoutDropZone: React.FC<LayoutDropZoneProps> = ({ id, target, orientation, emphasize, emptyLabel }) => {
  const { setNodeRef, isOver } = useDroppable({ id, data: { target } })

  if (orientation === 'row') {
    return (
      <Box
        ref={setNodeRef}
        data-testid={id}
        sx={{
          height: emphasize ? 64 : isOver ? 28 : 10,
          my: emphasize ? 0 : 0.25,
          borderRadius: 1,
          bgcolor: isOver ? 'info.main' : emphasize ? 'action.hover' : 'transparent',
          opacity: isOver ? 0.18 : 1,
          border: emphasize ? '2px dashed' : isOver ? '2px dashed' : 'none',
          borderColor: isOver ? 'info.main' : 'divider',
          transition: 'height 120ms ease, background-color 120ms ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {emphasize && (
          <Box component="span" sx={{ fontSize: 13, color: 'text.secondary', pointerEvents: 'none' }}>
            {emptyLabel || 'Drag a widget here to start building'}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box
      ref={setNodeRef}
      data-testid={id}
      sx={{
        width: isOver ? 24 : 10,
        mx: 0.25,
        alignSelf: 'stretch',
        borderRadius: 1,
        bgcolor: isOver ? 'info.main' : 'transparent',
        opacity: isOver ? 0.18 : 1,
        border: isOver ? '2px dashed' : 'none',
        borderColor: 'info.main',
        transition: 'width 120ms ease, background-color 120ms ease',
        flexShrink: 0,
      }}
    />
  )
}

export default LayoutDropZone
